<script setup lang="ts">
import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useCruiseStore } from '@/store/CruiseStore';
import AutoComplete from 'primevue/autocomplete';
import DatePicker from 'primevue/datepicker';
import Fluid from 'primevue/fluid';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import TreeSelect from 'primevue/treeselect';

const props = defineProps<{
  cruise: any;
  indexOption: number;
  isShowError?: boolean;
  advancedForm: object;
  validate: object;
  minDateValue: any;
  maxDateValue: any;
  listCabinCategory: any[];
  maxCategoryConfig?: number | null;
}>();

const emits = defineEmits<{
  'update:cruise': [value: any];
  updateDestination: [value: any];
  updateLineName: [value: any];
  updateShipName: [value: any];
}>();

const cruiseData = computed({
  get() {
    return props.cruise;
  },
  set(value) {
    emits('update:cruise', value);
  }
});

const minDateRangeValue = computed(() => {
  const getMin = props.minDateValue;
  return getMin ?? new Date(DateTime.utc().plus({ days: 60 }));
});

const maxDateRangeValue = computed(() => {
  const getMax = props.maxDateValue;
  return getMax ?? new Date(DateTime.utc().plus({ days: 360 }));
});

const cruiseStore = useCruiseStore();
const adminQuoteStore = useAdminQuoteStore();

const filterLineName = ref();
const filterShipName = ref();
const filterSailing = ref();
const minRetDate = ref();
const listAirline = ref([]);
const isInvalidDate = ref(false);
const isInvalidDuration = ref(false);

const vendors = computed(() => cruiseStore.selectVendors);
const sailings = computed(() => cruiseStore.getListSailing);
const ships = computed(() => cruiseStore.selectShips);
const vendorId = computed(() => cruiseStore.selectListVenDor);
const groupDestination = computed(() => cruiseStore.selectGroupDestination);

const getListAirline = () => {
  adminQuoteStore.getAirlines().then((res) => {
    listAirline.value = res;
  });
};
getListAirline();

const selectDestination = (destination: any) => {
  emits('updateDestination', destination);
  cruiseData.value.lineName = '';
  cruiseData.value.shipName = '';
  cruiseData.value.sailingName = '';
  const destinationSelected = Object.keys(destination)[0];
  const filter = groupDestination.value?.filter((f: any) => f.key.toString() === destinationSelected);
  if (!filter?.length) {
    groupDestination.value?.forEach((f) => {
      f.children?.forEach((c) => {
        if (c.key === destinationSelected) {
          cruiseStore.updateDestinationsCruiseSelectedId(c.value);
        }
        c.children?.forEach((gc) => {
          if (gc.key === destinationSelected) {
            cruiseStore.updateDestinationsCruiseSelectedId(gc.value);
          }
        });
      });
    });
    const payload: any[] = [];
    groupDestination.value?.forEach((g: any) => {
      g?.children?.forEach((c: any) => {
        if (c.key === destinationSelected) {
          const findDest = c.key[0] == g.key;
          if (findDest) {
            const formatDest = {
              destId: c.value,
              vendorIds: c.vendors
            };
            payload.push(formatDest);
            if (c?.children) {
              c.children.map((gc) => {
                const child = {
                  destId: gc.value,
                  vendorIds: gc.vendors
                };
                payload.push(child);
              });
            }
            cruiseStore.updateDestId(payload);
            cruiseStore.getNewVendors(payload);
          }
        }
        c?.children?.forEach((gc) => {
          if (gc.key === destinationSelected) {
            const findDest = gc.key.includes(g.key);
            if (findDest) {
              const formatDest = {
                destId: gc.value,
                vendorIds: gc.vendors
              };
              payload.push(formatDest);
              cruiseStore.updateDestId(payload);
              cruiseStore.getNewVendors(payload);
            }
          }
        });
      });
    });
  } else {
    cruiseStore.updateDestinationsCruiseSelectedId(filter[0].value);
    if (filter[0].value) {
      const payload = cruiseStore.formatSubDestination(filter);
      cruiseStore.updateDestId(payload);
      cruiseStore.getNewVendors(payload);
    }
  }
};

const searchLineName = (event: any) => {
  if (vendors.value?.length) {
    if (!event.query.trim().length) {
      filterLineName.value = [...vendors.value];
    } else {
      filterLineName.value = vendors.value.filter((v) => {
        return v.name.toLowerCase().startsWith(event.query.toLowerCase());
      });
    }
  }
};

const searchShipName = (event: any) => {
  if (ships.value?.length) {
    if (!event.query.trim().length) {
      filterShipName.value = [...ships.value];
    } else {
      filterShipName.value = ships.value.filter((v) => {
        return v.name.toLowerCase().startsWith(event.query.toLowerCase());
      });
    }
  }
};

const isSailingWithinDateRange = (sailing, start, end) => {
  const sailingDate = DateTime.fromISO(new Date(sailing.dep).toISOString());
  const startDate = DateTime.fromISO(new Date(start).toISOString());
  const endDate = DateTime.fromISO(new Date(end).toISOString());
  return sailingDate >= startDate && sailingDate <= endDate;
};

const filterSailingsByDateAndDuration = (startDate: string, endDate: string, duration?: number) => {
  return sailings.value.filter((s) => isSailingWithinDateRange(s, startDate, endDate) && (!duration || +s.dur === +duration));
};

const searchSailing = (event: any) => {
  if (!event.query.trim().length) {
    const startDate = DateTime.fromJSDate(new Date(cruiseData.value.sailDate)).toFormat('yyyy-LL-dd');
    const endDate = DateTime.fromJSDate(new Date(cruiseData.value.retDate)).toFormat('yyyy-LL-dd');
    if (!cruiseData.value.duration) {
      filterSailing.value = filterSailingsByDateAndDuration(startDate, endDate);
      isInvalidDate.value = !filterSailing.value.length;
      isInvalidDuration.value = false;
    } else {
      isInvalidDate.value = !filterSailingsByDateAndDuration(startDate, endDate).length;
      if (!isInvalidDate.value) {
        filterSailing.value = filterSailingsByDateAndDuration(startDate, endDate, cruiseData.value.duration);
        isInvalidDuration.value = !filterSailing.value.length;
      }
    }
    return;
  }
  if (!isInvalidDate.value && !isInvalidDuration.value) {
    filterSailing.value = sailings.value.filter((v) => v.sail.toLowerCase().startsWith(event.query.toLowerCase()));
  }
};

const eventShipName = (e: any) => {
  emits('updateShipName', e.value);
  cruiseData.value.sailingName = '';
  cruiseStore.updateShipSelectedId(e.value?.id);
  cruiseStore.getNewSailing();
};

const eventLineName = (e: any) => {
  emits('updateLineName', e.value);
  cruiseData.value.shipName = '';
  cruiseData.value.sailingName = '';
  cruiseStore.updateVendorId(e, e.value?.id);
  if (vendorId.value) {
    cruiseStore.getShips(vendorId.value);
  }
  cruiseStore.getNewSailing();
};

const handleEndDateChange = () => {
  cruiseData.value.sailingName = '';
};

const handleDateChange = (e) => {
  cruiseData.value.sailingName = '';
  cruiseData.value.retDate = e;
  minRetDate.value = new Date(cruiseData.value.sailDate);
};

const clearLineName = () => {
  cruiseData.value.lineName = '';
};

const clearShipName = () => {
  cruiseData.value.shipName = '';
};

const clearSailing = () => {
  cruiseData.value.sailingName = '';
};
</script>
<template>
  <div class="cruise-selection">
    <Fluid>
      <div class="field">
        <label for="dest"> {{ $t('advance_quote.form.destination') }}</label>
        <div class="select_destination">
          <TreeSelect
            v-model="cruiseData.destination"
            :options="groupDestination"
            :placeholder="$t('advance_quote.form.select_destination')"
            :disabled="indexOption != 0"
            @change="selectDestination($event)"
            :class="!cruiseData.destination && isShowError ? 'p-invalid w-full' : 'w-full'"
          />
        </div>
        <span class="text-red-500" v-if="isShowError"> {{ validate?.destination[0]?.$message }} </span>
      </div>
    </Fluid>
    <div class="grid">
      <div class="col-12 md:col-6">
        <Fluid>
          <div class="field">
            <label for="dest"> {{ $t('advance_quote.form.cruise_line_name') }}</label>
            <div class="flex align-items-center line_name">
              <AutoComplete
                v-model="cruiseData.lineName"
                :suggestions="filterLineName"
                :disabled="!cruiseData.destination || indexOption != 0"
                @complete="searchLineName($event)"
                :completeOnFocus="true"
                optionLabel="name"
                @change="eventLineName($event)"
                :class="isShowError && !cruiseData.lineName ? 'p-invalid' : ''"
                class="w-full"
                forceSelection
                id="lineName"
              />
              <div v-if="indexOption === 0">
                <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearLineName()"></i>
              </div>
            </div>
            <span class="text-red-500" v-if="isShowError"> {{ validate?.lineName[0]?.$message }} </span>
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-6">
        <Fluid>
          <div class="field">
            <label for="dest">{{ $t('advance_quote.form.cruise_ship_name') }}</label>
            <div class="flex align-items-center ship_name">
              <AutoComplete
                v-model="cruiseData.shipName"
                :suggestions="filterShipName"
                :disabled="!cruiseData.lineName || indexOption != 0"
                @complete="searchShipName($event)"
                completeOnFocus
                optionLabel="name"
                @change="eventShipName($event)"
                :class="isShowError && !cruiseData.shipName ? 'p-invalid' : ''"
                class="w-full"
                forceSelection
                id="shipName"
              />
              <div v-if="indexOption === 0">
                <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearShipName()"></i>
              </div>
            </div>
            <span class="text-red-500" v-if="isShowError"> {{ validate?.shipName[0]?.$message }} </span>
          </div>
        </Fluid>
      </div>
    </div>
    <div class="grid">
      <div class="col-12 md:col-3">
        <Fluid>
          <div class="field start_date">
            <label for="dest">{{ $t('create_quote.start_date') }}</label>
            <DatePicker
              v-model="cruiseData.sailDate"
              :minDate="minDateRangeValue"
              :maxDate="maxDateRangeValue"
              dateFormat="dd-M-yy"
              @update:modelValue="handleDateChange($event)"
              :class="(isShowError && !cruiseData.sailDate) || isInvalidDate ? 'p-invalid' : ''"
              :selectOtherMonths="true"
              :placeholder="$t('common.date_format')"
              :disabled="!advancedForm[0].groupTypeId"
            >
              <template #footer>
                <p class="text-center">
                  <small>{{ $t('common.press_space') }}</small>
                </p>
              </template>
            </DatePicker>
            <span class="text-red-500" v-if="isShowError"> {{ validate?.sailDate[0]?.$message }} </span>
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-3">
        <Fluid>
          <div class="field end_date">
            <label for="duration">{{ $t('create_quote.end_date') }}</label>
            <DatePicker
              v-model="cruiseData.retDate"
              :disabled="!cruiseData.sailDate || !advancedForm[0].groupTypeId"
              dateFormat="dd-M-yy"
              :minDate="minRetDate"
              :maxDate="maxDateRangeValue"
              @update:modelValue="handleEndDateChange($event)"
              :selectOtherMonths="true"
              :class="isInvalidDate ? 'p-invalid' : ''"
              :placeholder="$t('common.date_format')"
              id="cruiseEndDate"
            >
              <template #footer>
                <p class="text-center">
                  <small>{{ $t('common.press_space') }}</small>
                </p>
              </template>
            </DatePicker>
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-3">
        <Fluid>
          <div class="field cruise_duration">
            <label for="dest">{{ $t('advance_quote.form.duration') }}</label>
            <InputText v-model="cruiseData.duration" :placeholder="$t('advance_quote.form.duration')" :class="(isShowError && !cruiseData.duration) || isInvalidDuration ? 'p-invalid' : ''" />
            <span class="text-red-500" v-if="isShowError"> {{ validate?.duration[0]?.$message }} </span>
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-3">
        <Fluid>
          <div class="field number_cabins">
            <label for="number-cabin">{{ $t('advance_quote.form.number_of_cabin') }}</label>
            <InputText id="number-cabin" type="text" v-model="cruiseData.numberOfCabins" :class="isShowError && !cruiseData.numberOfCabins ? 'p-invalid' : ''" />
            <span class="text-red-500" v-if="isShowError"> {{ validate?.numberOfCabins[0]?.$message }} </span>
          </div>
        </Fluid>
      </div>
    </div>
    <Fluid>
      <div class="field">
        <label for="dest">{{ $t('advance_quote.form.cruise_sailing') }}</label>
        <div class="flex align-items-center cruise_sailling">
          <AutoComplete
            v-model="cruiseData.sailingName"
            :suggestions="filterSailing"
            :disabled="!cruiseData.shipName || !cruiseData.sailDate"
            @complete="searchSailing($event)"
            completeOnFocus
            optionLabel="sail"
            :class="(isShowError && !cruiseData.sailingName) || isInvalidDate || (isInvalidDuration && cruiseData.duration) ? 'p-invalid' : ''"
            class="w-full"
            forceSelection
            id="cruiseSailling"
          >
            <template #option="slotProps">
              <div>{{ slotProps.option.sail }}</div>
            </template>
          </AutoComplete>
          <div v-if="indexOption === 0">
            <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearSailing()"></i>
          </div>
        </div>
        <span class="text-red-500" v-if="isShowError"> {{ validate?.sailingName[0]?.$message }} </span>
        <span class="text-red-500" v-if="isInvalidDate">{{ $t('validate.wrong_date') }}</span>
        <br />
        <span class="text-red-500" v-if="isInvalidDuration">{{ $t('validate.wrong_duration') }}</span>
      </div>
    </Fluid>
    <Fluid>
      <div class="field">
        <label for="dest">{{ $t('advance_quote.form.cabin_category') }}</label>
        <MultiSelect
          v-model="cruiseData.cabinCategories"
          :options="listCabinCategory"
          :disabled="!cruiseData.shipName"
          optionLabel="displayTag"
          :selectionLimit="maxCategoryConfig ?? 1"
          :placeholder="$t('advance_quote.form.cabin_category')"
          display="chip"
          class="cabin-category"
          :class="isShowError && !cruiseData.cabinCategories?.length ? 'p-invalid' : ''"
        />
        <span class="text-red-500" v-if="isShowError"> {{ validate?.cabinCategories[0]?.$message }} </span>
      </div>
    </Fluid>
    <div class="grid">
      <div class="col-12 md:col-6">
        <Fluid>
          <div class="field">
            <label for="adt">{{ $t('advance_quote.form.adult') }}</label>
            <InputNumber :min="0" id="adt" type="text" v-model="cruiseData.adults" :class="isShowError && !cruiseData.adults ? 'p-invalid' : ''" />
            <span class="text-red-500" v-if="isShowError"> {{ validate?.adults[0]?.$message }} </span>
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-6">
        <Fluid>
          <div class="field">
            <label for="chd">{{ $t('advance_quote.form.children') }}</label>
            <InputNumber :min="0" id="chd" type="text" v-model="cruiseData.children" />
          </div>
        </Fluid>
      </div>
    </div>
  </div>
</template>
